import { WarningCircleIcon } from "@phosphor-icons/react/WarningCircle";
import { WarningIcon } from "@phosphor-icons/react/Warning";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { UUID } from "@/Shared/types";
import BookShipmentSavedLocationsDropdown from "../Dropdowns/BookShipmentSavedLocationsDropdown";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { LocationErrors } from "@/Shared/Locations/Validators/locationsErrorMessagesForLocation";
import { capitalize } from "@/Helpers/capitalize";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useState } from "react";
import BookShipmentSection from "./BookShipmentSection";
import BookShipmentLocationBox2 from "./BookShipmentLocationBox2";
import LocationsNewLocationDetails from "@/Shared/Locations/CustomComponents/LocationsNewLocationDetails";

function extractErrorMessages(errorMessages: LocationErrors) {
  if (errorMessages.locationType) {
    return errorMessages.locationType;
  }

  if (errorMessages.address.postalCode) {
    return errorMessages.address.postalCode;
  }

  return undefined;
}

interface LocationSection2Props {
  location: AddLocationLocationType;
  selectedSavedLocationId: UUID | undefined;
  enteringNew: boolean;
  savedLocations: Array<SavedLocation>;
  defaultLocationId: undefined | UUID;
  onSelectSavedLocation: (
    savedLocationId: UUID,
    savedLocation: Location,
  ) => void;
  onUpdateLocation: (location: AddLocationLocationType) => void;
  onNewLocation: () => void;
  errorMessages: LocationErrors;
  warningMessages: LocationErrors;
  forceValidation: boolean;
  index: number;
  id: string;
  context: LocationContext;
  onSelectFromAddressBook: () => void;
  disablePostalCodeEntry: boolean;
  pickupDate: string;
}

interface LocationSectionDescriptionProps {
  enteringNew: boolean;
  onSelectFromAddressBook: () => void;
  context: LocationContext;
  savedLocations: Array<SavedLocation>;
}

function LocationSectionDescription_(props: LocationSectionDescriptionProps) {
  if (props.enteringNew) {
    if (props.savedLocations.length > 0) {
      return (
        <>
          Enter a new {props.context} location{" "}
          <Link onClick={props.onSelectFromAddressBook}>
            or select one from your address book
          </Link>
        </>
      );
    } else {
      return <>Enter the details of your {props.context} location</>;
    }
  } else {
    return <>Select a location from your address book.</>;
  }
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
function BookShipmentLocationSection2(props: LocationSection2Props) {
  const [transitioning, setTransitioning] = useState(false);
  const {
    enteringNew,
    selectedSavedLocationId,
    location,
    savedLocations,
    context,
    onSelectFromAddressBook,
  } = props;

  const extractedErrorMessages = extractErrorMessages(props.errorMessages);
  const extractedWarningMessages = extractErrorMessages(props.warningMessages);

  const selectedSavedLocation = selectedSavedLocationId
    ? savedLocations.find(
        (sl) => sl.savedLocationId === selectedSavedLocationId,
      )
    : undefined;

  return (
    <BookShipmentSection
      index={props.index}
      header={`${capitalize(props.context)} Location`}
      id={props.id}
      description={
        <LocationSectionDescription_
          enteringNew={enteringNew}
          onSelectFromAddressBook={function () {
            setTransitioning(true);
            setTimeout(function () {
              onSelectFromAddressBook();
              setTransitioning(false);
            }, 250);
          }}
          savedLocations={savedLocations}
          context={context}
        />
      }
      minHeight={props.context === LocationContext.Pickup ? "414px" : "495px"}
    >
      {/* No sizing box around the spinner — the section's own minHeight
            already reserves the space, so the page doesn't collapse during the
            250ms swap between the address book and the new-location form. */}
      {transitioning && <Spinner />}
      {!transitioning && !enteringNew && selectedSavedLocationId && (
        // Stretch, so the summary card is the same width as every other panel
        // on the form. Centring here would shrink-wrap it to its own content
        // and leave this one card narrower than the rest.
        <Stack gap={4} align="stretch">
          <BookShipmentSavedLocationsDropdown
            data-testid={`${props.context}-saved-locations-dropdown`}
            savedLocations={savedLocations}
            savedLocationId={selectedSavedLocationId}
            defaultLocationId={props.defaultLocationId}
            context={props.context}
            onSetSavedLocationId={function (newSavedLocationId) {
              const newSavedLocation = savedLocations.find(
                (sl) => sl.savedLocationId === newSavedLocationId,
              );
              if (newSavedLocation === undefined) {
                throw new Error("No newSavedLocation. Should never get here");
              }
              props.onSelectSavedLocation(
                newSavedLocationId,
                newSavedLocation.location,
              );
            }}
            onAddLocation={function () {
              setTransitioning(true);
              setTimeout(function () {
                props.onNewLocation();
                setTransitioning(false);
              }, 250);
            }}
          />
          <BookShipmentLocationBox2
            location={location}
            context={props.context}
            locationHasDock={selectedSavedLocation?.locationHasDock}
            locationHasForklift={selectedSavedLocation?.locationHasForklift}
          />
          {props.forceValidation && extractedErrorMessages && (
            <Row gap={2}>
              <Icon icon={WarningCircleIcon} weight="fill" color="text-red" />
              <T size="lg" weight="medium" color="red">
                {extractedErrorMessages}
              </T>
            </Row>
          )}
          {props.forceValidation &&
            !extractedErrorMessages &&
            extractedWarningMessages && (
              <Row gap={2}>
                <Icon icon={WarningIcon} weight="fill" color="text-orange" />
                <T size="lg" weight="medium">
                  {extractedWarningMessages}
                </T>
              </Row>
            )}
        </Stack>
      )}
      {!transitioning && enteringNew && (
        <LocationsNewLocationDetails
          locationErrorMessages={props.errorMessages}
          locationWarningMessages={props.warningMessages}
          context={props.context}
          setLocation={props.onUpdateLocation}
          location={location}
          forceValidation={props.forceValidation}
          disablePostalCodeEntry={props.disablePostalCodeEntry}
          pickupDate={props.pickupDate}
        />
      )}
    </BookShipmentSection>
  );
}
export default BookShipmentLocationSection2;
